/**
 * SOLID Review
 * S — Strong. One job: detect mutations still in flight past a threshold and report start and end through callbacks — never
 *     touching a store itself.
 * O — Good. The threshold and the callbacks are handed in; what counts as slow, and what happens then, change without this file.
 * L — N/A — no inheritance hierarchy; it holds an `ApolloLink`, it is not one.
 * I — Strong. One link, one options interface.
 * D — Strong. Apollo's link primitives, the callbacks and the timers it is handed — no application or UI import.
 */
import { ApolloLink, Observable, type FetchResult } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import type { IServiceTimers } from '../platform/IServiceTimers';

/**
 * Slow-request detector (§12.36). Load testing showed the backend degrades GRACEFULLY under saturation — it gets slow rather
 * than erroring (p95 604 ms normally, but p95 2.5 s / p99 3.3 s past the knee). Individual buttons already show their own
 * in-flight state ("Announcing…", "Passing…"), which covers the normal case; what was missing is a signal for the ABNORMAL
 * case, where a player would otherwise conclude the game is broken and start refreshing — the worst thing they can do to an
 * already-loaded server.
 *
 * This watches every MUTATION (queries and subscriptions are background traffic and would produce noisy banners) and reports
 * "still in flight after thresholdMs" so the UI can show a transient hint. It counts concurrent slow operations so the hint
 * clears only when the LAST one finishes.
 *
 * Deliberately UI-agnostic: it takes callbacks, so the store and the rendering stay out of the network layer.
 */
export interface IServiceSlowRequestLinkOptions {
  /** How long a mutation may run before it is considered slow. */
  thresholdMs?: number;
  /** Called when the first slow mutation crosses the threshold. */
  onSlowStart: () => void;
  /** Called when the last slow mutation finishes. */
  onSlowEnd: () => void;
}

const DEFAULT_THRESHOLD_MS = 1500;

/**
 * THE SLOW-REQUEST LINK AS AN OBJECT (frontend-infrastructure-oo P2, 2026-10-28, §28.287) — `createSlowRequestLink`'s closure as
 * fields, on the timers the composition root hands it. Behaviour unchanged.
 */
export class SlowRequestLink {
  /** The link the Apollo client chains first. */
  readonly link: ApolloLink;
  private slowCount = 0;

  constructor(private readonly options: IServiceSlowRequestLinkOptions, private readonly timers: IServiceTimers) {
    this.link = new ApolloLink((operation, forward) => {
      const def = getMainDefinition(operation.query);
      const isMutation = def.kind === 'OperationDefinition' && def.operation === 'mutation';
      if (!isMutation) return forward(operation);
      return this.watched(forward(operation));
    });
  }

  private watched(source: ReturnType<ApolloLink.ForwardFunction>): Observable<FetchResult> {
    return new Observable<FetchResult>((observer) => {
      let wasSlow = false;
      let settled = false;
      const timer = this.timers.setTimeout(() => { wasSlow = true; this.markSlow(); }, this.options.thresholdMs ?? DEFAULT_THRESHOLD_MS);

      // Idempotent: unsubscribe can fire after complete/error, and double-counting would strand the hint on screen forever.
      const settle = (): void => {
        if (settled) return;
        settled = true;
        this.timers.clearTimeout(timer);
        this.clearSlow(wasSlow);
      };

      const sub = source.subscribe({
        next: (value) => observer.next(value),
        error: (err) => { settle(); observer.error(err); },
        complete: () => { settle(); observer.complete(); },
      });

      return () => { settle(); sub.unsubscribe(); };
    });
  }

  private markSlow(): void {
    this.slowCount += 1;
    if (this.slowCount === 1) this.options.onSlowStart();
  }

  private clearSlow(wasSlow: boolean): void {
    if (!wasSlow) return;
    this.slowCount = Math.max(0, this.slowCount - 1);
    if (this.slowCount === 0) this.options.onSlowEnd();
  }
}
